import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './Routes.css';

const AdminRoutes = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Placeholder routes data
  const allRoutes = [
    {
      id: 'RT001',
      fromCity: 'Lagos',
      fromCode: 'LOS',
      toCity: 'London',
      toCode: 'LHR',
      airline: 'British Airways',
      duration: '6h 10m',
      status: 'Active',
      type: 'International'
    },
    {
      id: 'RT002',
      fromCity: 'Abuja',
      fromCode: 'ABV',
      toCity: 'Lagos',
      toCode: 'LOS',
      airline: 'Air Peace',
      duration: '1h 30m',
      status: 'Active',
      type: 'Domestic'
    },
    {
      id: 'RT003',
      fromCity: 'Lagos',
      fromCode: 'LOS',
      toCity: 'Dubai',
      toCode: 'DXB',
      airline: 'Emirates',
      duration: '7h 30m',
      status: 'Active',
      type: 'International'
    },
    {
      id: 'RT004',
      fromCity: 'Lagos',
      fromCode: 'LOS',
      toCity: 'Accra',
      toCode: 'ACC',
      airline: 'Arik Air',
      duration: '1h 15m',
      status: 'Active',
      type: 'Domestic'
    },
    {
      id: 'RT005',
      fromCity: 'Lagos',
      fromCode: 'LOS',
      toCity: 'New York',
      toCode: 'JFK',
      airline: 'Lufthansa',
      duration: '11h 45m',
      status: 'Active',
      type: 'International'
    },
    {
      id: 'RT006',
      fromCity: 'Port Harcourt',
      fromCode: 'PHC',
      toCity: 'Lagos',
      toCode: 'LOS',
      airline: 'Dana Air',
      duration: '1h 15m',
      status: 'Inactive',
      type: 'Domestic'
    },
    {
      id: 'RT007',
      fromCity: 'Abuja',
      fromCode: 'ABV',
      toCity: 'Dubai',
      toCode: 'DXB',
      airline: 'Qatar Airways',
      duration: '7h 15m',
      status: 'Active',
      type: 'International'
    },
    {
      id: 'RT008',
      fromCity: 'Kano',
      fromCode: 'KAN',
      toCity: 'Lagos',
      toCode: 'LOS',
      airline: 'Overland Airways',
      duration: '1h 45m',
      status: 'Active',
      type: 'Domestic'
    },
    {
      id: 'RT009',
      fromCity: 'Lagos',
      fromCode: 'LOS',
      toCity: 'Istanbul',
      toCode: 'IST',
      airline: 'Turkish Airlines',
      duration: '6h 30m',
      status: 'Active',
      type: 'International'
    },
    {
      id: 'RT010',
      fromCity: 'Uyo',
      fromCode: 'QUO',
      toCity: 'Lagos',
      toCode: 'LOS',
      airline: 'Ibom Air',
      duration: '1h 45m',
      status: 'Inactive',
      type: 'Domestic'
    },
    {
      id: 'RT011',
      fromCity: 'Lagos',
      fromCode: 'LOS',
      toCity: 'Addis Ababa',
      toCode: 'ADD',
      airline: 'Ethiopian Airlines',
      duration: '5h 30m',
      status: 'Active',
      type: 'International'
    },
    {
      id: 'RT012',
      fromCity: 'Abuja',
      fromCode: 'ABV',
      toCity: 'Kano',
      toCode: 'KAN',
      airline: 'Max Air',
      duration: '1h 15m',
      status: 'Active',
      type: 'Domestic'
    }
  ];

  // Filter routes based on search and filters
  const filteredRoutes = allRoutes.filter(route => {
    const matchesSearch = 
      route.fromCity.toLowerCase().includes(searchQuery.toLowerCase()) ||
      route.toCity.toLowerCase().includes(searchQuery.toLowerCase()) ||
      route.airline.toLowerCase().includes(searchQuery.toLowerCase()) ||
      route.fromCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      route.toCode.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      route.status.toLowerCase() === statusFilter.toLowerCase();
    
    const matchesType = typeFilter === 'all' || 
      route.type.toLowerCase() === typeFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredRoutes.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRoutes = filteredRoutes.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleAddRoute = () => {
    navigate('/admin/routes/add');
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting routes to CSV...');
  };

  const handleViewRoute = (routeId) => {
    // Placeholder for view route
    console.log('View route:', routeId);
  };

  const handleEditRoute = (routeId) => {
    // Placeholder for edit route
    console.log('Edit route:', routeId);
  };

  const handleToggleRoute = (routeId) => {
    // Placeholder for disable/enable route
    console.log('Toggle route:', routeId);
  };

  return (
    <AdminLayout pageTitle="Routes">
      <div className="admin-routes-page">
        {/* Page Title */}
        <div className="admin-routes-header">
          <h1 className="admin-routes-title">Routes</h1>
          <p className="admin-routes-subtitle">Manage flight routes</p>
        </div>

        {/* Top Control Bar */}
        <div className="admin-routes-controls">
          <div className="admin-controls-left">
            <button
              className="admin-add-route-button"
              onClick={handleAddRoute}
            >
              + Add Route
            </button>
            
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by from/to city or airline..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              className="admin-filter-select"
              value={typeFilter}
              onChange={handleTypeFilterChange}
            >
              <option value="all">All Types</option>
              <option value="domestic">Domestic</option>
              <option value="international">International</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <button className="admin-export-button" onClick={handleExport}>
              <span className="admin-export-icon">📥</span>
              Export CSV
            </button>
          </div>
        </div>

        {/* Routes Table */}
        {paginatedRoutes.length > 0 ? (
          <>
            <div className="admin-routes-table-wrapper">
              {/* Desktop Table */}
              <table className="admin-routes-table">
                <thead>
                  <tr>
                    <th>Route ID</th>
                    <th>From Airport</th>
                    <th>To Airport</th>
                    <th>Airline</th>
                    <th>Flight Duration</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRoutes.map((route) => (
                    <tr key={route.id} className="admin-routes-table-row">
                      <td className="admin-table-cell admin-table-id">{route.id}</td>
                      <td className="admin-table-cell">
                        <div className="admin-airport-info">
                          <span className="admin-airport-city">{route.fromCity}</span>
                          <span className="admin-airport-code">{route.fromCode}</span>
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-airport-info">
                          <span className="admin-airport-city">{route.toCity}</span>
                          <span className="admin-airport-code">{route.toCode}</span>
                        </div>
                      </td>
                      <td className="admin-table-cell">{route.airline}</td>
                      <td className="admin-table-cell">{route.duration}</td>
                      <td className="admin-table-cell">
                        <span className={`admin-status-badge admin-status-${route.status.toLowerCase()}`}>
                          {route.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-actions-menu">
                          <button
                            className="admin-action-button"
                            onClick={() => handleViewRoute(route.id)}
                          >
                            View
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleEditRoute(route.id)}
                          >
                            Edit
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleToggleRoute(route.id)}
                          >
                            {route.status === 'Active' ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="admin-routes-cards">
                {paginatedRoutes.map((route) => (
                  <div key={route.id} className="admin-route-card">
                    <div className="admin-route-card-header">
                      <div className="admin-route-card-id">{route.id}</div>
                      <span className={`admin-status-badge admin-status-${route.status.toLowerCase()}`}>
                        {route.status}
                      </span>
                    </div>
                    <div className="admin-route-card-body">
                      <div className="admin-route-card-route">
                        <div className="admin-route-card-airport">
                          <span className="admin-route-card-city">{route.fromCity}</span>
                          <span className="admin-route-card-code">{route.fromCode}</span>
                        </div>
                        <div className="admin-route-card-arrow">→</div>
                        <div className="admin-route-card-airport">
                          <span className="admin-route-card-city">{route.toCity}</span>
                          <span className="admin-route-card-code">{route.toCode}</span>
                        </div>
                      </div>
                      <div className="admin-route-card-row">
                        <span className="admin-route-card-label">Airline:</span>
                        <span className="admin-route-card-value">{route.airline}</span>
                      </div>
                      <div className="admin-route-card-row">
                        <span className="admin-route-card-label">Duration:</span>
                        <span className="admin-route-card-value">{route.duration}</span>
                      </div>
                      <div className="admin-route-card-row">
                        <span className="admin-route-card-label">Type:</span>
                        <span className="admin-route-card-value">{route.type}</span>
                      </div>
                    </div>
                    <div className="admin-route-card-actions">
                      <select
                        className="admin-mobile-actions-select"
                        onChange={(e) => {
                          const action = e.target.value;
                          if (action === 'view') handleViewRoute(route.id);
                          else if (action === 'edit') handleEditRoute(route.id);
                          else if (action === 'toggle') handleToggleRoute(route.id);
                          e.target.value = '';
                        }}
                      >
                        <option value="">Actions...</option>
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                        <option value="toggle">{route.status === 'Active' ? 'Disable' : 'Enable'}</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredRoutes.length)} of {filteredRoutes.length} routes
              </div>
              <div className="admin-pagination-controls">
                <select
                  className="admin-rows-select"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="admin-page-numbers">
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="admin-page-ellipsis">...</span>}
                          <button
                            className={`admin-page-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="admin-empty-state">
            <div className="admin-empty-icon">🛫</div>
            <h3 className="admin-empty-title">No routes found</h3>
            <p className="admin-empty-text">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'Get started by adding your first route'}
            </p>
            <button
              className="admin-empty-action-button"
              onClick={handleAddRoute}
            >
              + Add Route
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRoutes;

